import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { settingsService } from '../settingsService';

export function useSettings() {
  return useQuery({
    queryKey: ['settings'],
    queryFn: () => settingsService.getSettings(),
    staleTime: 300000,
  });
}

export function useUpdateSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: settingsService.updateSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
  });
}

export function useBulkUpdateSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: settingsService.bulkUpdateSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      queryClient.invalidateQueries({ queryKey: ['public-settings'] });
    },
  });
}

/** Public branding settings, available even before the user logs in. */
export function usePublicSettings() {
  return useQuery({
    queryKey: ['public-settings'],
    queryFn: () => settingsService.getPublicSettings(),
    staleTime: 300000,
    retry: 1,
  });
}

/**
 * Keeps the dashboard browser tab icon in sync with the favicon configured on
 * the Settings → General page. Falls back to the hospital logo and finally to
 * the bundled dashboard icon so the tab never shows a blank/missing favicon.
 */
export function useBrandFavicon() {
  const { data } = usePublicSettings();
  const settings = data?.data ?? {};
  const href =
    (settings.hospital_favicon || '').trim() ||
    (settings.hospital_logo || '').trim() ||
    '/favicon.png';

  useEffect(() => {
    const links = document.querySelectorAll<HTMLLinkElement>(
      'link[rel="icon"], link[rel="shortcut icon"]',
    );
    if (links.length > 0) {
      links.forEach((link) => link.setAttribute('href', href));
      return;
    }
    const link = document.createElement('link');
    link.rel = 'icon';
    link.href = href;
    document.head.appendChild(link);
  }, [href]);
}
